import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { ORIGENES } from '../constants/origenes';
import { 
  ClipboardList, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  AlertTriangle,
  Activity,
  TrendingUp,
  Filter,
  User,
  Building2,
  Layers,
  MapPin,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import PasswordExpirationWarning from '../components/PasswordExpirationWarning';

// Constantes para caché
const CACHE_KEY = 'dashboard_cache';
const CACHE_DURATION = 60000; // 1 minuto

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const initialLoadDone = useRef(false);
  
  // Estados para aviso de expiración de contraseña
  const [showPasswordWarning, setShowPasswordWarning] = useState(false);
  const [daysUntilExpiration, setDaysUntilExpiration] = useState(null);

  // Filter states
  const [supervisores, setSupervisores] = useState([]);
  const [proyectos, setProyectos] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [etapasPorProyecto, setEtapasPorProyecto] = useState({});
  const [selectedSupervisor, setSelectedSupervisor] = useState('all');
  const [selectedProyecto, setSelectedProyecto] = useState('all');
  const [selectedEtapas, setSelectedEtapas] = useState([]);
  const [etapasLoading, setEtapasLoading] = useState(false);
  
  // Origen filter: default all except "Acta de Entrega" and "Observación de Entrega"
  const DEFAULT_ORIGENES = ORIGENES.filter(o => o !== 'Acta de Entrega' && o !== 'Observación de Entrega');
  const [selectedOrigenes, setSelectedOrigenes] = useState(DEFAULT_ORIGENES);

  const isSupervisor = user?.rol_nombre === 'Supervisor';
  
  // Verificar si hay aviso de expiración de contraseña
  useEffect(() => {
    if (user?.days_until_expiration !== undefined && user?.days_until_expiration <= 15) {
      setShowPasswordWarning(true);
      setDaysUntilExpiration(user.days_until_expiration);
    }
  }, [user]);
  
  const handleChangePassword = () => {
    navigate('/perfil'); // o donde tengas el cambio de contraseña
    toast.info('Redirigiendo a cambio de contraseña...');
  };
  
  const handleDismissWarning = () => {
    setShowPasswordWarning(false);
    // Guardar en localStorage que el usuario cerró el aviso (opcional)
    localStorage.setItem('password_warning_dismissed', Date.now().toString());
  };

  // Función para obtener caché
  const getCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_DURATION) {
          return data;
        }
      }
    } catch (e) {
      console.error('Error reading cache:', e);
    }
    return null;
  }, []);

  // Función para guardar caché
  const setCache = useCallback((data) => {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('Error saving cache:', e);
    }
  }, []);

  // Carga inicial con Stale-While-Revalidate
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    const loadInitial = async () => {
      // 1. Mostrar caché inmediatamente si existe
      const cached = getCache();
      if (cached) {
        setSupervisores(cached.filters?.supervisores || []);
        setProyectos(cached.filters?.proyectos || []);
        setEtapas(cached.filters?.etapas || []);
        setEtapasPorProyecto(cached.filters?.etapas_por_proyecto || {});
        setStats(cached.stats);
        setLoading(false);
        
        // Si es supervisor, establecer su ID
        if (isSupervisor && user?.id) {
          setSelectedSupervisor(user.id);
        }
      }

      // 2. Cargar datos frescos en segundo plano
      try {
        const defaultOrigenes = ORIGENES.filter(o => o !== 'Acta de Entrega' && o !== 'Observación de Entrega');
        const response = await dashboardAPI.getAll({ origenes: defaultOrigenes });
        const data = response.data;
        
        // Guardar en caché
        setCache(data);
        
        // Actualizar estado solo si hay cambios significativos
        setSupervisores(data.filters?.supervisores || []);
        setProyectos(data.filters?.proyectos || []);
        setEtapas(data.filters?.etapas || []);
        setEtapasPorProyecto(data.filters?.etapas_por_proyecto || {});
        setStats(data.stats);
        
        // Si es supervisor, establecer su ID
        if (isSupervisor && user?.id) {
          setSelectedSupervisor(user.id);
        }
      } catch (error) {
        if (!cached) {
          toast.error('Error al cargar dashboard');
        }
      } finally {
        setLoading(false);
      }
    };

    loadInitial();
  }, [getCache, setCache, isSupervisor, user?.id]);

  // Cargar stats cuando cambian los filtros (después de la carga inicial)
  const loadFilteredStats = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    
    try {
      const filters = {};
      if (selectedSupervisor && selectedSupervisor !== 'all') {
        filters.supervisor_id = selectedSupervisor;
      }
      if (selectedProyecto && selectedProyecto !== 'all') {
        filters.proyecto_id = selectedProyecto;
      }
      if (selectedEtapas.length > 0) {
        filters.etapas = selectedEtapas;
      }
      if (selectedOrigenes.length > 0 && selectedOrigenes.length < ORIGENES.length) {
        filters.origenes = selectedOrigenes;
      } else if (selectedOrigenes.length > 0) {
        filters.origenes = selectedOrigenes;
      }

      const response = await dashboardAPI.getAll(filters);
      setStats(response.data.stats);
      
      // Actualizar etapas disponibles del response
      if (response.data.filters?.etapas) {
        setEtapas(response.data.filters.etapas);
      }
      if (response.data.filters?.etapas_por_proyecto) {
        setEtapasPorProyecto(response.data.filters.etapas_por_proyecto);
      }
      setEtapasLoading(false);
      
      // Solo actualizar caché si no hay filtros
      const noFilters = !filters.supervisor_id && !filters.proyecto_id && !filters.etapas && selectedOrigenes.length === DEFAULT_ORIGENES.length;
      if (noFilters) {
        setCache(response.data);
      }
    } catch (error) {
      if (!silent) toast.error('Error al cargar estadísticas');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [selectedSupervisor, selectedProyecto, selectedEtapas, selectedOrigenes, setCache, DEFAULT_ORIGENES.length]);

  // Etapas disponibles según proyecto seleccionado
  const etapasOptions = useMemo(() => {
    let available;
    if (selectedProyecto && selectedProyecto !== 'all' && etapasPorProyecto[selectedProyecto]) {
      available = etapasPorProyecto[selectedProyecto];
    } else {
      available = etapas;
    }
    return available.map(e => ({ value: e, label: e }));
  }, [selectedProyecto, etapasPorProyecto, etapas]);

  // Opciones de origen
  const origenesOptions = useMemo(() => 
    ORIGENES.map(o => ({ value: o, label: o })),
  []);

  // Resetear etapas seleccionadas cuando cambia el proyecto
  const handleProyectoChange = useCallback((value) => {
    setSelectedProyecto(value);
    setSelectedEtapas([]);
    if (value !== 'all') setEtapasLoading(true);
  }, []);

  // Recargar cuando cambian los filtros
  useEffect(() => {
    // Skip en la carga inicial
    if (!initialLoadDone.current) return;
    
    loadFilteredStats(true);
  }, [selectedSupervisor, selectedProyecto, selectedEtapas, selectedOrigenes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to filtered solicitudes
  const navigateToSolicitudes = (estadoFilter, diasFilter = null) => {
    const params = new URLSearchParams();
    if (estadoFilter) params.set('estado', estadoFilter);
    if (diasFilter !== null) params.set('dias', diasFilter);
    if (selectedSupervisor && selectedSupervisor !== 'all') params.set('supervisor', selectedSupervisor);
    if (selectedProyecto && selectedProyecto !== 'all') params.set('proyecto', selectedProyecto);
    if (selectedEtapas.length > 0) params.set('etapas', selectedEtapas.join(','));
    if (selectedOrigenes.length > 0 && selectedOrigenes.length < ORIGENES.length) params.set('origenes', selectedOrigenes.join(','));
    
    navigate(`/solicitudes?${params.toString()}`);
  };

  const getPrioridadLabel = (prioridad) => {
    if (prioridad === 1) return { text: 'Alta', color: 'text-red-600', bg: 'bg-red-100' };
    if (prioridad === 2) return { text: 'Media', color: 'text-yellow-600', bg: 'bg-yellow-100' };
    return { text: 'Baja', color: 'text-green-600', bg: 'bg-green-100' };
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 lg:h-12 lg:w-12 border-b-2 border-[#9d8272]"></div>
      </div>
    );
  }

  const kanban = stats?.kanban || {};

  // Clickable stat card component
  const ClickableStatCard = ({ children, onClick, className = "" }) => (
    <div 
      onClick={onClick}
      className={`cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-md active:scale-[0.98] ${className}`}
    >
      {children}
    </div>
  );

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2D2D2D] mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
          Dashboard
        </h1>
        <p className="text-sm lg:text-base text-[#5A5A5A]">
          ¡Bienvenido, {user?.nombre}!
        </p>
      </div>

      {/* Aviso de expiración de contraseña */}
      {showPasswordWarning && daysUntilExpiration !== null && (
        <PasswordExpirationWarning
          daysUntilExpiration={daysUntilExpiration}
          onChangePassword={handleChangePassword}
          onDismiss={handleDismissWarning}
        />
      )}

      {/* Filters Section */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm">
        <CardContent className="p-3 lg:p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-[#9d8272]" />
            <span className="text-sm font-medium text-[#5A5A5A]">Filtros</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Supervisor Filter */}
            <div className="space-y-1">
              <label className="text-xs text-[#888888] flex items-center gap-1">
                <User size={12} />
                Supervisor
              </label>
              <Select 
                value={selectedSupervisor} 
                onValueChange={setSelectedSupervisor}
                disabled={isSupervisor}
              >
                <SelectTrigger 
                  className={`h-9 text-sm ${isSupervisor ? 'bg-[#F5F5F5] cursor-not-allowed' : ''}`}
                  data-testid="filter-supervisor"
                >
                  <SelectValue placeholder="Todos los supervisores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los supervisores</SelectItem>
                  {supervisores.map((sup) => (
                    <SelectItem key={sup.id} value={sup.id}>
                      {sup.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Proyecto Filter */}
            <div className="space-y-1">
              <label className="text-xs text-[#888888] flex items-center gap-1">
                <Building2 size={12} />
                Proyecto
              </label>
              <Select value={selectedProyecto} onValueChange={handleProyectoChange}>
                <SelectTrigger className="h-9 text-sm" data-testid="filter-proyecto">
                  <SelectValue placeholder="Todos los proyectos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los proyectos</SelectItem>
                  {proyectos.map((proy) => (
                    <SelectItem key={proy.id} value={proy.id}>
                      {proy.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Etapa Filter - Multi-select (disabled when no project selected) */}
            <MultiSelect
              options={etapasOptions}
              selected={selectedEtapas}
              onChange={setSelectedEtapas}
              placeholder="Todas las etapas"
              icon={Layers}
              label="Etapa"
              testId="filter-etapa"
              disabled={selectedProyecto === 'all'}
              loading={etapasLoading}
            />

            {/* Origen Filter - Multi-select */}
            <MultiSelect
              options={origenesOptions}
              selected={selectedOrigenes}
              onChange={setSelectedOrigenes}
              placeholder="Todos los orígenes"
              icon={MapPin}
              label="Origen"
              testId="filter-origen"
            />
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
        {/* Total Requerimientos */}
        <ClickableStatCard onClick={() => navigateToSolicitudes(null)}>
          <Card className="bg-gradient-to-br from-[#9d8272] to-[#7a6459] text-white shadow-lg h-full" data-testid="total-card">
            <CardContent className="p-4 lg:p-5">
              <div className="flex items-center justify-between mb-2">
                <ClipboardList size={20} className="opacity-80" />
                <ExternalLink size={14} className="opacity-60" />
              </div>
              <p className="text-3xl lg:text-4xl font-bold">{stats?.total_solicitudes || 0}</p>
              <p className="text-xs mt-1 opacity-80">Total Requerimientos</p>
            </CardContent>
          </Card>
        </ClickableStatCard>

        {/* Vigentes */}
        <ClickableStatCard onClick={() => navigateToSolicitudes('vigentes')}>
          <Card className="bg-gradient-to-br from-[#8c926f] to-[#6b7050] text-white shadow-lg h-full" data-testid="vigentes-card">
            <CardContent className="p-4 lg:p-5">
              <div className="flex items-center justify-between mb-2">
                <Activity size={20} className="opacity-80" />
                <ExternalLink size={14} className="opacity-60" />
              </div>
              <p className="text-3xl lg:text-4xl font-bold">{stats?.vigentes || 0}</p>
              <p className="text-xs mt-1 opacity-80">Vigentes</p>
            </CardContent>
          </Card>
        </ClickableStatCard>

        {/* Terminados */}
        <ClickableStatCard onClick={() => navigateToSolicitudes('terminado')}>
          <Card className="bg-gradient-to-br from-[#9ca3af] to-[#6b7280] text-white shadow-lg h-full" data-testid="terminados-card">
            <CardContent className="p-4 lg:p-5">
              <div className="flex items-center justify-between mb-2">
                <CheckCircle2 size={20} className="opacity-80" />
                <ExternalLink size={14} className="opacity-60" />
              </div>
              <p className="text-3xl lg:text-4xl font-bold">{stats?.terminados || 0}</p>
              <p className="text-xs mt-1 opacity-80">Terminados</p>
            </CardContent>
          </Card>
        </ClickableStatCard>

        {/* Cerrados */}
        <ClickableStatCard onClick={() => navigateToSolicitudes('cerrado')}>
          <Card className="bg-gradient-to-br from-[#6b7280] to-[#4b5563] text-white shadow-lg h-full" data-testid="cerrados-card">
            <CardContent className="p-4 lg:p-5">
              <div className="flex items-center justify-between mb-2">
                <XCircle size={20} className="opacity-80" />
                <ExternalLink size={14} className="opacity-60" />
              </div>
              <p className="text-3xl lg:text-4xl font-bold">{stats?.cerrados || 0}</p>
              <p className="text-xs mt-1 opacity-80">Cerrados</p>
            </CardContent>
          </Card>
        </ClickableStatCard>
      </div>

      {/* Kanban Timeline Board */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm overflow-hidden">
        <div className="p-4 lg:p-5 border-b border-[#E8E8E7] bg-gradient-to-r from-[#FAFAFA] to-white">
          <div className="flex items-center gap-2">
            <TrendingUp size={20} className="text-[#9d8272]" />
            <h2 className="text-lg lg:text-xl font-semibold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
              Pipeline de Requerimientos Vigentes
            </h2>
          </div>
          <p className="text-xs lg:text-sm text-[#888888] mt-1">
            Haz clic en cualquier tarjeta para ver los requerimientos
          </p>
        </div>

        {/* Kanban Container */}
        <div className="p-3 lg:p-5">
          <div className="flex flex-col lg:flex-row lg:items-stretch gap-3 lg:gap-0">
            
            {/* Column 1: T1 - Tramo coordinación visita - Verde claro #A8C8C7 */}
            <div className="flex-1 lg:pr-4">
              {/* Header */}
              <div className="rounded-t-lg bg-[#A8C8C7] px-3 py-2 lg:px-4 lg:py-3">
                <h3 className="text-sm lg:text-base font-semibold text-[#2D4A4A]">
                  T1: Tramo coord. visita
                </h3>
              </div>
              
              {/* Content */}
              <div className="bg-[#E8F4F3] rounded-b-lg p-3 lg:p-4 min-h-[140px] lg:min-h-[180px] border border-[#A8C8C7]">
                <div className="space-y-2 lg:space-y-3">
                  {/* Solicitadas */}
                  <ClickableStatCard onClick={() => navigateToSolicitudes('solicitado')}>
                    <div className="bg-white rounded-lg p-3 shadow-sm border border-[#C5DCDB] hover:border-[#67A09F] transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <ClipboardList size={16} className="text-[#67A09F]" />
                          <span className="text-xs lg:text-sm text-[#5A5A5A]">Solicitadas</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg lg:text-xl font-bold text-[#2D2D2D]">
                            {kanban.t1?.solicitado || 0}
                          </span>
                          <ExternalLink size={12} className="text-[#888888]" />
                        </div>
                      </div>
                    </div>
                  </ClickableStatCard>
                  
                  {/* Inspección Pendiente */}
                  <ClickableStatCard onClick={() => navigateToSolicitudes('inspeccion_pendiente')}>
                    <div className="bg-white rounded-lg p-3 shadow-sm border border-[#C5DCDB] hover:border-[#67A09F] transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Clock size={16} className="text-[#67A09F]" />
                          <span className="text-xs lg:text-sm text-[#5A5A5A]">Insp. Pendiente</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg lg:text-xl font-bold text-[#2D2D2D]">
                            {kanban.t1?.inspeccion_pendiente || 0}
                          </span>
                          <ExternalLink size={12} className="text-[#888888]" />
                        </div>
                      </div>
                    </div>
                  </ClickableStatCard>
                </div>
                
                {/* Percentage Footer */}
                <div className="mt-4 pt-3 border-t border-[#A8C8C7]">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-2 flex-1 bg-[#C5DCDB] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#67A09F] rounded-full transition-all duration-500"
                        style={{ width: `${kanban.t1?.porcentaje || 0}%` }}
                      />
                    </div>
                    <span className="text-lg lg:text-xl font-bold text-[#2D4A4A] min-w-[50px] text-right">
                      {kanban.t1?.porcentaje || 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline Separator 1 (0 days) */}
            <div className="hidden lg:flex flex-col items-center justify-start py-2">
              <div className="bg-[#67A09F] text-white text-xs font-bold px-2 py-1 rounded-full shadow-md z-10">
                0
              </div>
              <div className="flex-1 border-l-2 border-dashed border-[#67A09F] my-2" />
              <div className="text-[10px] text-[#888888]">días</div>
            </div>

            {/* Mobile Timeline Indicator */}
            <div className="lg:hidden flex items-center gap-2 py-2">
              <div className="flex-1 border-t-2 border-dashed border-[#67A09F]" />
              <span className="bg-[#67A09F] text-white text-xs font-bold px-3 py-1 rounded-full">0 días</span>
              <div className="flex-1 border-t-2 border-dashed border-[#67A09F]" />
            </div>

            {/* Column 2: T2/T3 Verde (≤30 días) - Verde oscuro #67A09F */}
            <div className="flex-1 lg:px-4">
              {/* Headers */}
              <div className="rounded-t-lg bg-[#67A09F] px-3 py-2 lg:px-4 lg:py-3 flex">
                <div className="flex-1 text-center border-r border-[#4A8584]">
                  <h3 className="text-xs lg:text-sm font-semibold text-white">T2: Diagnóstico</h3>
                </div>
                <div className="flex-1 text-center">
                  <h3 className="text-xs lg:text-sm font-semibold text-white">T3: Ejecución</h3>
                </div>
              </div>
              
              {/* Content */}
              <div className="bg-[#D4E8E7] rounded-b-lg p-3 lg:p-4 min-h-[140px] lg:min-h-[180px] border border-[#67A09F]">
                <div className="flex gap-2 lg:gap-3">
                  {/* T2 Column */}
                  <div className="flex-1 space-y-2">
                    <ClickableStatCard onClick={() => navigateToSolicitudes('inspeccion_programada', 'lte30')}>
                      <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm border border-[#A8C8C7] hover:border-[#67A09F] transition-colors">
                        <div className="flex items-center justify-between gap-1">
                          <div className="flex items-center gap-1">
                            <AlertTriangle size={14} className="text-[#67A09F] flex-shrink-0" />
                            <span className="text-[10px] lg:text-xs text-[#5A5A5A] truncate">Insp. Prog.</span>
                          </div>
                          <span className="text-base lg:text-lg font-bold text-[#2D2D2D]">
                            {kanban.t2_t3_verde?.inspeccion_programada || 0}
                          </span>
                        </div>
                      </div>
                    </ClickableStatCard>
                    <ClickableStatCard onClick={() => navigateToSolicitudes('atencion_pendiente', 'lte30')}>
                      <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm border border-[#A8C8C7] hover:border-[#67A09F] transition-colors">
                        <div className="flex items-center justify-between gap-1">
                          <div className="flex items-center gap-1">
                            <Clock size={14} className="text-[#67A09F] flex-shrink-0" />
                            <span className="text-[10px] lg:text-xs text-[#5A5A5A] truncate">Atenc. Pend.</span>
                          </div>
                          <span className="text-base lg:text-lg font-bold text-[#2D2D2D]">
                            {kanban.t2_t3_verde?.atencion_pendiente || 0}
                          </span>
                        </div>
                      </div>
                    </ClickableStatCard>
                  </div>
                  
                  {/* T3 Column */}
                  <div className="flex-1">
                    <ClickableStatCard onClick={() => navigateToSolicitudes('atencion_proceso', 'lte30')}>
                      <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm border border-[#A8C8C7] hover:border-[#67A09F] transition-colors h-full flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <Activity size={14} className="text-[#67A09F] flex-shrink-0" />
                          <span className="text-[10px] lg:text-xs text-[#5A5A5A]">En Proceso</span>
                        </div>
                        <span className="text-base lg:text-lg font-bold text-[#2D2D2D]">
                          {kanban.t2_t3_verde?.atencion_proceso || 0}
                        </span>
                      </div>
                    </ClickableStatCard>
                  </div>
                </div>
                
                {/* Percentage Footer */}
                <div className="mt-4 pt-3 border-t border-[#67A09F]">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-2 flex-1 bg-[#A8C8C7] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#67A09F] rounded-full transition-all duration-500"
                        style={{ width: `${kanban.t2_t3_verde?.porcentaje || 0}%` }}
                      />
                    </div>
                    <span className="text-lg lg:text-xl font-bold text-[#2D4A4A] min-w-[50px] text-right">
                      {kanban.t2_t3_verde?.porcentaje || 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Timeline Separator 2 (30 days) */}
            <div className="hidden lg:flex flex-col items-center justify-start py-2">
              <div className="bg-[#C00000] text-white text-xs font-bold px-2 py-1 rounded-full shadow-md z-10">
                30
              </div>
              <div className="flex-1 border-l-2 border-dashed border-[#C00000] my-2" />
              <div className="text-[10px] text-[#888888]">días</div>
            </div>

            {/* Mobile Timeline Indicator */}
            <div className="lg:hidden flex items-center gap-2 py-2">
              <div className="flex-1 border-t-2 border-dashed border-[#C00000]" />
              <span className="bg-[#C00000] text-white text-xs font-bold px-3 py-1 rounded-full">30 días</span>
              <div className="flex-1 border-t-2 border-dashed border-[#C00000]" />
            </div>

            {/* Column 3: T2/T3 Rojo (>30 días) - Rojo #C00000 */}
            <div className="flex-1 lg:pl-4">
              {/* Headers */}
              <div className="rounded-t-lg bg-[#C00000] px-3 py-2 lg:px-4 lg:py-3 flex">
                <div className="flex-1 text-center border-r border-[#A00000]">
                  <h3 className="text-xs lg:text-sm font-semibold text-white">T2: Diagnóstico</h3>
                </div>
                <div className="flex-1 text-center">
                  <h3 className="text-xs lg:text-sm font-semibold text-white">T3: Ejecución</h3>
                </div>
              </div>
              
              {/* Content */}
              <div className="bg-[#FCEAEA] rounded-b-lg p-3 lg:p-4 min-h-[140px] lg:min-h-[180px] border border-[#C00000]">
                <div className="flex gap-2 lg:gap-3">
                  {/* T2 Column */}
                  <div className="flex-1 space-y-2">
                    <ClickableStatCard onClick={() => navigateToSolicitudes('inspeccion_programada', 'gt30')}>
                      <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm border border-[#F5C6C6] hover:border-[#C00000] transition-colors">
                        <div className="flex items-center justify-between gap-1">
                          <div className="flex items-center gap-1">
                            <AlertTriangle size={14} className="text-[#C00000] flex-shrink-0" />
                            <span className="text-[10px] lg:text-xs text-[#5A5A5A] truncate">Insp. Prog.</span>
                          </div>
                          <span className="text-base lg:text-lg font-bold text-[#C00000]">
                            {kanban.t2_t3_rojo?.inspeccion_programada || 0}
                          </span>
                        </div>
                      </div>
                    </ClickableStatCard>
                    <ClickableStatCard onClick={() => navigateToSolicitudes('atencion_pendiente', 'gt30')}>
                      <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm border border-[#F5C6C6] hover:border-[#C00000] transition-colors">
                        <div className="flex items-center justify-between gap-1">
                          <div className="flex items-center gap-1">
                            <Clock size={14} className="text-[#C00000] flex-shrink-0" />
                            <span className="text-[10px] lg:text-xs text-[#5A5A5A] truncate">Atenc. Pend.</span>
                          </div>
                          <span className="text-base lg:text-lg font-bold text-[#C00000]">
                            {kanban.t2_t3_rojo?.atencion_pendiente || 0}
                          </span>
                        </div>
                      </div>
                    </ClickableStatCard>
                  </div>
                  
                  {/* T3 Column */}
                  <div className="flex-1">
                    <ClickableStatCard onClick={() => navigateToSolicitudes('atencion_proceso', 'gt30')}>
                      <div className="bg-white rounded-lg p-2 lg:p-3 shadow-sm border border-[#F5C6C6] hover:border-[#C00000] transition-colors h-full flex items-center justify-between">
                        <div className="flex items-center gap-1">
                          <Activity size={14} className="text-[#C00000] flex-shrink-0" />
                          <span className="text-[10px] lg:text-xs text-[#5A5A5A]">En Proceso</span>
                        </div>
                        <span className="text-base lg:text-lg font-bold text-[#C00000]">
                          {kanban.t2_t3_rojo?.atencion_proceso || 0}
                        </span>
                      </div>
                    </ClickableStatCard>
                  </div>
                </div>
                
                {/* Percentage Footer */}
                <div className="mt-4 pt-3 border-t border-[#E8A0A0]">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-2 flex-1 bg-[#F5C6C6] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#C00000] rounded-full transition-all duration-500"
                        style={{ width: `${kanban.t2_t3_rojo?.porcentaje || 0}%` }}
                      />
                    </div>
                    <span className="text-lg lg:text-xl font-bold text-[#8B0000] min-w-[50px] text-right">
                      {kanban.t2_t3_rojo?.porcentaje || 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Priority Requests */}
      <Card className="bg-white border border-[#E8E8E7] shadow-sm">
        <div className="p-4 lg:p-5 border-b border-[#E8E8E7]">
          <h2 className="text-lg lg:text-xl font-semibold text-[#2D2D2D]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Requerimientos Prioritarios
          </h2>
          <p className="text-xs lg:text-sm text-[#888888] mt-1">Ordenados por antigüedad y prioridad</p>
        </div>
        <CardContent className="p-3 lg:p-5">
          {stats?.solicitudes_prioritarias && stats.solicitudes_prioritarias.length > 0 ? (
            <div className="space-y-2 lg:space-y-3">
              {stats.solicitudes_prioritarias.slice(0, 5).map((solicitud) => {
                const prioridad = getPrioridadLabel(solicitud.prioridad);
                return (
                  <div
                    key={solicitud.id}
                    onClick={() => navigate(`/solicitudes?id=${solicitud.id}`)}
                    className="p-3 lg:p-4 border border-[#E8E8E7] rounded-lg hover:bg-[#FAFAFA] hover:border-[#9d8272] transition-colors cursor-pointer"
                    data-testid="priority-request-item"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-semibold text-[#2D2D2D] text-sm lg:text-base truncate">
                            {solicitud.proyecto_nombre}
                          </h4>
                        </div>
                        <p className="text-xs lg:text-sm text-[#5A5A5A] mb-1">
                          Depto {solicitud.departamento}
                        </p>
                        <div className="flex items-center flex-wrap gap-3 text-[10px] lg:text-xs text-[#888888]">
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {solicitud.dias_antiguedad || 0} días
                          </span>
                          <span className="truncate">{solicitud.cliente_nombre}</span>
                          <span className="capitalize px-2 py-0.5 bg-[#F5F5F5] rounded text-[#5A5A5A]">
                            {solicitud.estado?.replace('_', ' ')}
                          </span>
                        </div>
                      </div>
                      <ExternalLink size={16} className="text-[#888888] flex-shrink-0" />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center text-[#888888] py-8 text-sm">No hay requerimientos pendientes</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
